// components/EmployeeListScreen.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EmployeeService from './EmployeeService';
import './EmployeeListScreen.css';

const EmployeeListScreen = ({ userData = {} }) => {
  const navigate = useNavigate();
  const currentUserId = userData.id || localStorage.getItem('userId');
  
  // Create a ref to track API calls
  const apiCallComplete = useRef(false);
  
  // State for employees data
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for filter
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  
  // Function to check if user can assign tasks
  const canAssignTask = () => {
    const userRoleId = localStorage.getItem("user_role_id");
    const isWrite = localStorage.getItem("is_write");
    
    // Hide assign task button if role_id is 3 and is_write is 0
    return !(userRoleId === "3" && isWrite === "0");
  };
  
  // Fetch employees from API
  useEffect(() => {
    const fetchEmployees = async () => {
      // Only fetch if we haven't already completed this API call
      if (!apiCallComplete.current) {
        try {
          setLoading(true);
          
          // Use EmployeeService to fetch employee data
          const data = await EmployeeService.fetchAllEmployeeDetails();
          setEmployees(data);
          
          // Mark that we've completed the API call
          apiCallComplete.current = true;
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching employees:', err);
          setError('Failed to load employees. Please try again.');
          setLoading(false);
        }
      }
    };
    
    fetchEmployees();
  }, []);
  
  // Get unique departments for filter
  const departments = useMemo(() => {
    if (!employees.length) return ['All'];
    
    // Extract unique department names
    const uniqueDepartments = [...new Set(employees.map(emp => emp.aa_app_resource_deptname))];
    
    // Return with 'All' as the first option
    return ['All', ...uniqueDepartments].filter(Boolean);
  }, [employees]);
  
  // Filter employees
  const filteredEmployees = useMemo(() => {
    if (!employees.length) return [];
    
    return employees.filter(employee => {
      // Don't display the current user in the list (if ID matches)
      if (employee.aa_app_employee_id === currentUserId) {
        return false;
      }
      
      // Department filter
      if (departmentFilter !== 'All' && employee.aa_app_resource_deptname !== departmentFilter) {
        return false;
      }
      
      // Search filter (case insensitive)
      if (searchTerm && !employee.aa_app_employee_name.toLowerCase().includes(searchTerm.toLowerCase()) && 
          !employee.aa_app_employee_email.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [employees, departmentFilter, searchTerm, currentUserId]);
  
  const handleBack = () => {
    navigate('/dashboard');
  };
  
  const handleAssignTask = (employeeId, employeeName) => {
    // Navigate to the create task page with this employee pre-selected
    navigate('/create-task', { 
      state: { 
        preSelectedEmployee: { 
          id: employeeId, 
          name: employeeName 
        } 
      } 
    });
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="employee-list-screen">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading employees...</p>
        </div>
      </div>
    );
  }
  
  // Show error message if there's an error
  if (error) {
    return (
      <div className="employee-list-screen">
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Improved inline styles for better alignment
  const filterStyles = {
    container: {
      margin: '20px 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end', // Changed from 'center' to 'flex-end' for better alignment
      gap: '20px',
      flexWrap: 'wrap'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '5px'
    },
    label: {
      fontWeight: 'bold',
      fontSize: '12px',
      whiteSpace: 'nowrap'
    },
    searchInput: {
      padding: '6px 10px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      width: '220px',
      height: '32px',
      fontSize: '12px',
      fontFamily: 'inherit'
    },
    select: {
      width: '180px',
      height: '32px',
      fontSize: '12px',
      fontFamily: 'inherit',
      padding: '6px 10px',
      borderRadius: '4px',
      border: '1px solid #ccc'
    },
    clearButton: {
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontFamily: 'inherit',
      marginTop: '16px' // Adjusted for smaller inputs
    }
  };
  
  return (
    <div className="employee-list-screen">
      <div className="screen-header">
        <h1 className="screen-title">Employees</h1>
        <button 
          className="btn btn-secondary"
          onClick={handleBack}
        >
          Back
        </button>
      </div>
      
      {/* Updated filter section with better alignment */}
      <div style={filterStyles.container}>
        <div style={filterStyles.filterGroup}>
          <label style={filterStyles.label}>Search</label>
          <input 
            type="text" 
            placeholder="Search by name or email..."
            style={filterStyles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div style={filterStyles.filterGroup}>
          <label style={filterStyles.label}>Department</label>
          <select 
            style={filterStyles.select}
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
          >
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>

        <button 
          className="btn btn-secondary"
          style={filterStyles.clearButton}
          onClick={() => {
            setDepartmentFilter('All');
            setSearchTerm('');
          }}
          title="Clear all filters"
        >
          Clear Filters
        </button>
      </div>
      
      <div className="employees-table-container">
        {filteredEmployees.length === 0 ? (
          <div className="no-employees-message">
            <p>No employees found matching your filters.</p>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setDepartmentFilter('All');
                setSearchTerm('');
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <table className="employees-table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>Active Tasks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee, index) => (
                <tr key={index}>
                  <td>{employee.aa_app_employee_id || 'N/A'}</td>
                  <td>{employee.aa_app_employee_name}</td>
                  <td>{employee.aa_app_employee_email}</td>
                  <td>{employee.aa_app_resource_deptname}</td>
                  <td>
                    <span className={`task-count ${employee.aa_app_active_task_count > 0 ? 'active' : 'none'}`}>
                      {employee.aa_app_active_task_count}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {/* Conditionally render Assign Task button */}
                      {canAssignTask() && (
                        <button 
                          className="btn-icon assign"
                          onClick={() => handleAssignTask(employee.aa_app_employee_id, employee.aa_app_employee_name)}
                          title="Assign Task"
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EmployeeListScreen;